import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { deriveNotificationRows } from 'features/admin/protocol-editor/notification-templates/deriveNotificationRows';
import type { DerivedNotificationRow } from 'features/admin/protocol-editor/notification-templates/types';

type ProtocolState = {
  rows: DerivedNotificationRow[];
  status: 'idle' | 'loading' | 'ready' | 'error';
  error?: string;
  requestId?: string;
};

export type AdminDerivedNotificationsState = {
  byProtocol: Record<string, ProtocolState>;
};

const initialState: AdminDerivedNotificationsState = {
  byProtocol: {},
};

type ThunkArg = { protocolId: string; dslSource: string };

type ThunkResult = {
  protocolId: string;
  rows: DerivedNotificationRow[];
};

type ThunkState = { adminDerivedNotifications: AdminDerivedNotificationsState };

export const adminDeriveNotificationRowsThunk = createAsyncThunk<
  ThunkResult,
  ThunkArg,
  { state: ThunkState; rejectValue: string }
>(
  'adminDerivedNotifications/deriveRows',
  async ({ protocolId, dslSource }, { rejectWithValue }) => {
    const result = await deriveNotificationRows(dslSource);
    if (result.kind === 'error') {
      return rejectWithValue(result.error);
    }
    return { protocolId, rows: result.rows };
  }
);

const adminDerivedNotificationsSlice = createSlice({
  name: 'adminDerivedNotifications',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(adminDeriveNotificationRowsThunk.pending, (state, action) => {
        const { protocolId } = action.meta.arg;
        const prior = state.byProtocol[protocolId];
        state.byProtocol[protocolId] = {
          rows: prior?.rows ?? [],
          status: 'loading',
          requestId: action.meta.requestId,
        };
      })
      .addCase(adminDeriveNotificationRowsThunk.fulfilled, (state, action) => {
        const { protocolId, rows } = action.payload;
        if (state.byProtocol[protocolId]?.requestId !== action.meta.requestId)
          return;
        state.byProtocol[protocolId] = {
          rows,
          status: 'ready',
          requestId: action.meta.requestId,
        };
      })
      .addCase(adminDeriveNotificationRowsThunk.rejected, (state, action) => {
        const { protocolId } = action.meta.arg;
        if (state.byProtocol[protocolId]?.requestId !== action.meta.requestId)
          return;
        state.byProtocol[protocolId] = {
          rows: [],
          status: 'error',
          error: action.payload ?? action.error.message ?? 'Unknown error',
          requestId: action.meta.requestId,
        };
      });
  },
});

export const adminDerivedNotificationsReducer =
  adminDerivedNotificationsSlice.reducer;
